import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import styles from "./ConfigEditor.module.css";
import { Button, Form, Input, Radio, Space } from "antd";
import { ACCOUNT_TYPES_CONFIG_KEY, BUSINESS_TYPES_CONFIG_KEY, parseIncomeTypeOptions } from "@/utils/incomeTypes";

interface ConfigEditorProps {
  initialValues: AddConfigParams;
  onSubmit: (values: AddConfigParams) => Promise<void>;
  onCancel: () => void;
}

function highlightJson(value: string): ReactNode[] {
  const tokens: ReactNode[] = [];
  const pattern = /"(?:\\.|[^"\\])*"|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?|\b(?:true|false|null)\b/g;
  let offset = 0;
  for (const match of Array.from(value.matchAll(pattern))) {
    const start = match.index ?? 0;
    const token = match[0];
    tokens.push(value.slice(offset, start));
    const isKey = token.startsWith('"') && /^\s*:/.test(value.slice(start + token.length));
    const color = isKey ? "#9cdcfe" : token.startsWith('"') ? "#ce9178"
      : /^(true|false|null)$/.test(token) ? "#569cd6" : "#b5cea8";
    tokens.push(<span key={start} style={{ color }}>{token}</span>);
    offset = start + token.length;
  }
  tokens.push(value.slice(offset));
  return tokens;
}

export default function ConfigEditor({ initialValues, onSubmit, onCancel }: ConfigEditorProps): JSX.Element {
  const [form] = Form.useForm<AddConfigParams>();
  const [saving, setSaving] = useState(false);
  const configValue: string | undefined = Form.useWatch("configValue", form);
  const jsonPreview = useMemo((): ReactNode => {
    const value = configValue ?? initialValues.configValue ?? "";
    if (!value.trim()) return "暂无内容";
    try {
      const parsed: unknown = JSON.parse(value);
      return highlightJson(JSON.stringify(parsed, null, 2));
    } catch {
      return "当前键值不是有效 JSON";
    }
  }, [configValue, initialValues.configValue]);

  const handleSubmit = async (values: AddConfigParams): Promise<void> => {
    setSaving(true);
    try {
      await onSubmit({ ...values, configId: initialValues.configId });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form form={form} layout="vertical" initialValues={initialValues} onFinish={handleSubmit}>
      <div className={styles.columns}>
        <div className={styles.fields}>
          <Form.Item label="参数键名" name="configKey" rules={[{ required: true, whitespace: true, message: "请填写参数键名" }]}>
            <Input placeholder="请输入参数键名" />
          </Form.Item>
          <Form.Item label="参数名称" name="configName" rules={[{ required: true, whitespace: true, message: "请填写参数名称" }]}>
            <Input placeholder="请输入参数名称" />
          </Form.Item>
          <Form.Item label="参数键值" name="configValue" dependencies={["configKey"]} rules={[
            { required: true, whitespace: true, message: "请填写参数键值" },
            ({ getFieldValue }) => ({
              validator: (_rule: unknown, value: string): Promise<void> => {
                if (!value?.trim()) return Promise.resolve();
                try {
                  const key: unknown = getFieldValue("configKey");
                  if (key === ACCOUNT_TYPES_CONFIG_KEY) parseIncomeTypeOptions(value, "accountTypes");
                  if (key === BUSINESS_TYPES_CONFIG_KEY) parseIncomeTypeOptions(value, "businessTypes");
                  return Promise.resolve();
                } catch (error: unknown) {
                  return Promise.reject(error);
                }
              },
            }),
          ]}>
            <Input.TextArea rows={9} placeholder="请输入参数键值" style={{ fontFamily: "monospace" }} />
          </Form.Item>
          <Form.Item label="系统内置" name="configType">
            <Radio.Group options={[{ label: "是", value: "Y" }, { label: "否", value: "N" }]} />
          </Form.Item>
          <Form.Item label="备注" name="remark">
            <Input.TextArea rows={3} placeholder="请输入备注" />
          </Form.Item>
        </div>
        <div className={styles.preview}>
          <div className={styles.previewLabel}>JSON 格式化参考</div>
          <pre aria-label="JSON 格式化参考" tabIndex={0} className={styles.code}>{jsonPreview}</pre>
        </div>
      </div>
      <div className={styles.footer}>
        <Space>
          <Button onClick={onCancel}>取消</Button>
          <Button type="primary" htmlType="submit" loading={saving}>确认</Button>
        </Space>
      </div>
    </Form>
  );
}
